import { getBackendUrl } from '@/lib/backendUrl';
import { getApiError } from '@/lib/utils';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CheckCircle2, CircleDashed, FileCheck2, SearchX, Download, Upload, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import MonthShortcuts from './MonthShortcuts';

const BLOCKING_LABELS = {
  no_site_on_job: 'No site recorded on this job.',
  no_auditors_assigned: 'No auditors are assigned to this job.',
  no_billable_lines: 'No billable service lines on this job.',
  missing_or_ambiguous_target: 'has no resolved QBO target.',
  not_yet_verified_in_qbo: 'is mapped but not yet verified active in QBO.',
  rate_missing_or_unverified: 'Cellar Ledger monthly rate for this site is not set and verified.',
  no_months_selected: 'No Cellar Ledger months selected yet.',
  month_already_billed: 'already marked Billed at this site.',
  already_exported: 'was already exported — redownload the original instead of creating a new invoice number.',
};

const API = getBackendUrl();

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function formatDateOnly(isoDateStr) {
  if (!isoDateStr) return null;
  const [y, m, d] = isoDateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB');
}

export default function ExportPanel() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState({});
  const [startingNumber, setStartingNumber] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [registerUploaded, setRegisterUploaded] = useState(false);
  const datesEditedRef = useRef(false);
  const fileInputRef = useRef(null);
  const requestIdRef = useRef(0);

  const loadJobs = useCallback(async (from, to) => {
    const requestId = ++requestIdRef.current;
    setLoadingJobs(true);
    try {
      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const { data } = await axios.get(`${API}/api/sales-invoicing/jobs`, { params, headers: authHeaders() });
      if (requestId !== requestIdRef.current) return; // a newer request has since started — this response is stale
      if (!datesEditedRef.current) {
        setFromDate(data.from_date);
        setToDate(data.to_date);
      }
      setJobs(data.jobs || []);
      setRegisterUploaded(!!data.register_uploaded);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setJobs([]);
      toast.error(getApiError(err, 'Failed to load completed jobs.'));
    } finally {
      if (requestId === requestIdRef.current) setLoadingJobs(false);
    }
  }, []);

  useEffect(() => { loadJobs(undefined, undefined); }, [loadJobs]);

  const editDate = (setter) => (e) => { datesEditedRef.current = true; setter(e.target.value); };
  const applyFilters = () => { datesEditedRef.current = true; loadJobs(fromDate, toDate); setSelected({}); setPreview(null); };
  const applyShortcut = (from, to) => {
    datesEditedRef.current = true;
    setFromDate(from);
    setToDate(to);
    loadJobs(from, to);
    setSelected({});
    setPreview(null);
  };

  const selectedIds = Object.keys(selected).filter((id) => selected[id]).map(Number);
  const toggleAll = (checked) => {
    const next = {};
    jobs.forEach((j) => { next[j.job_id] = checked; });
    setSelected(next);
    setPreview(null);
  };
  const toggleOne = (jobId, checked) => { setSelected((s) => ({ ...s, [jobId]: checked })); setPreview(null); };

  const invalidStartingNumber = startingNumber !== '' && !(Number.isInteger(Number(startingNumber)) && Number(startingNumber) > 0);

  const runPreview = async () => {
    if (selectedIds.length === 0) { toast.error('Select at least one completed job first.'); return; }
    if (invalidStartingNumber || startingNumber === '') { toast.error('Enter a manual starting invoice number greater than 0.'); return; }
    setPreviewing(true);
    try {
      const { data } = await axios.post(`${API}/api/sales-invoicing/export/preview`, {
        job_ids: selectedIds, starting_invoice_number: Number(startingNumber),
      }, { headers: authHeaders() });
      setPreview(data);
    } catch (err) {
      toast.error(getApiError(err, 'Could not preview this export.'));
    } finally {
      setPreviewing(false);
    }
  };

  const runConfirm = async () => {
    setDownloading(true);
    try {
      const resp = await axios.post(`${API}/api/sales-invoicing/export/confirm`, {
        job_ids: selectedIds, starting_invoice_number: Number(startingNumber),
      }, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_invoicing_staging_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Staging file downloaded — nothing was sent or posted to QBO.');
      setPreview(null);  // this confirm already recorded a batch — a second click must re-preview first
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          toast.error(getApiError({ response: { data: parsed } }, 'This export is no longer ready — re-preview it.'));
        } catch {
          toast.error('This export is no longer ready — re-preview it.');
        }
      } else {
        toast.error(getApiError(err, 'This export is no longer ready — re-preview it.'));
      }
    } finally {
      setDownloading(false);
    }
  };

  const redownloadBatch = async (batchId) => {
    try {
      const res = await axios.get(`${API}/api/sales-invoicing/export/batches/${batchId}/download`, {
        headers: authHeaders(), responseType: 'blob',
      });
      const match = /filename="?([^"]+)"?/.exec(res.headers['content-disposition'] || '');
      const filename = match ? match[1] : 'sales_invoicing_staging.xlsx';
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          err.response.data = JSON.parse(text);
        } catch { /* leave as-is if it isn't JSON */ }
      }
      toast.error(getApiError(err, 'Could not redownload this export.'));
    }
  };

  const uploadRegister = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(`${API}/api/qbo/register/upload`, form, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success('QBO invoice register uploaded.');
      setRegisterUploaded(true);
      setPreview(null);  // possible-duplicate checks may now differ — re-preview before confirming
    } catch (err) {
      toast.error(getApiError(err, 'Could not upload this register.'));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const previewById = {};
  (preview?.rows_preview || []).forEach((r) => { previewById[r.job_id] = r; });

  return (
    <div data-testid="export-panel">
      <div className="rounded-lg border border-sky-200 bg-sky-50 text-sky-900 text-sm px-4 py-3 mb-4" data-testid="export-scope-notice">
        A staging file only, for review — nothing here connects to, posts to, or reserves a number in QBO. Register upload is optional.
        This XLSX is formatted for QBO's Spreadsheet Sync (Excel) tool — QBO's separate web "Import invoices" feature only accepts CSV
        and will misread this workbook if used there; this is not a claim that QBO has read, accepted or imported this file.
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="export-filters">
        <div>
          <Label htmlFor="exp-from-date" className="text-xs text-slate-500">From</Label>
          <Input id="exp-from-date" type="date" value={fromDate} onChange={editDate(setFromDate)} data-testid="export-from-date" className="w-[160px]" />
        </div>
        <div>
          <Label htmlFor="exp-to-date" className="text-xs text-slate-500">To</Label>
          <Input id="exp-to-date" type="date" value={toDate} onChange={editDate(setToDate)} data-testid="export-to-date" className="w-[160px]" />
        </div>
        <Button variant="outline" onClick={applyFilters} data-testid="export-apply-filters-btn">Apply</Button>
        <MonthShortcuts onSelect={applyShortcut} testIdPrefix="export" />

        <div>
          <Label htmlFor="exp-starting-number" className="text-xs text-slate-500">Manual starting invoice number</Label>
          <Input
            id="exp-starting-number" type="number" min="1" placeholder="e.g. 1042"
            value={startingNumber} onChange={(e) => { setStartingNumber(e.target.value); setPreview(null); }}
            data-testid="export-starting-invoice-number" className="w-[200px]"
          />
          {invalidStartingNumber && <p className="text-xs text-red-600 mt-1" data-testid="export-starting-invoice-error">Must be a whole number greater than 0.</p>}
        </div>

        <div className="flex flex-col gap-1">
          <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={uploadRegister} data-testid="export-register-file-input" />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="export-upload-register-btn">
            <Upload className="h-3.5 w-3.5 mr-1" />{registerUploaded ? 'Replace QBO register' : 'Upload QBO register (optional)'}
          </Button>
        </div>
      </div>

      {!registerUploaded && (
        <div className="text-xs text-slate-400 mb-3" data-testid="export-no-register-note">
          No QBO invoice register has been uploaded — possible-duplicate checks are skipped. Export is not blocked by this.
        </div>
      )}

      <div className="overflow-x-auto mb-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={jobs.length > 0 && jobs.every((j) => selected[j.job_id])}
                  onCheckedChange={(c) => toggleAll(!!c)}
                  data-testid="export-select-all-checkbox"
                />
              </TableHead>
              <TableHead>Site</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Region</TableHead>
              <TableHead>Preview status</TableHead>
              <TableHead className="text-right">Invoice #</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadingJobs && <TableRow><TableCell colSpan={6} className="text-center text-slate-500">Loading…</TableCell></TableRow>}
            {!loadingJobs && jobs.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8" data-testid="export-jobs-empty">
                <SearchX className="h-5 w-5 mx-auto mb-1 text-slate-300" />No completed jobs in this range.
              </TableCell></TableRow>
            )}
            {!loadingJobs && jobs.map((j) => {
              const p = previewById[j.job_id];
              return (
                <TableRow key={j.job_id} data-testid={`export-job-row-${j.job_id}`}>
                  <TableCell>
                    <Checkbox checked={!!selected[j.job_id]} onCheckedChange={(c) => toggleOne(j.job_id, !!c)} data-testid={`export-job-checkbox-${j.job_id}`} />
                  </TableCell>
                  <TableCell className="font-medium">{j.hotel_name || '—'}</TableCell>
                  <TableCell className="text-sm text-slate-500">{formatDateOnly(j.scheduled_date)}</TableCell>
                  <TableCell><Badge variant="secondary">{j.region}</Badge></TableCell>
                  <TableCell>
                    {!p ? (
                      <span className="text-xs text-slate-400">Preview to check readiness</span>
                    ) : p.is_ready ? (
                      <Badge className="bg-emerald-600" data-testid={`export-job-ready-badge-${j.job_id}`}><CheckCircle2 className="h-3 w-3 mr-1" />Ready</Badge>
                    ) : (
                      <div>
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800" data-testid={`export-job-blocked-badge-${j.job_id}`}>
                          <CircleDashed className="h-3 w-3 mr-1" />Blocked — {(p.blocking_decisions || []).length} issue(s)
                        </Badge>
                        <ul className="mt-1 space-y-0.5" data-testid={`export-job-blocking-reasons-${j.job_id}`}>
                          {(p.blocking_decisions || []).map((b, idx) => (
                            <li key={idx} className="text-xs text-amber-800 flex items-center gap-1 flex-wrap">
                              {b.label ? `${b.label} ` : ''}{BLOCKING_LABELS[b.reason] || b.reason.replace(/_/g, ' ')}
                              {b.kind === 'already_exported' && b.has_workbook && (
                                <Button size="sm" variant="outline" className="h-5 px-1.5 text-[11px]" onClick={() => redownloadBatch(b.batch_id)} data-testid={`export-redownload-btn-${j.job_id}`}>
                                  Redownload original
                                </Button>
                              )}
                              {b.kind === 'already_exported' && !b.has_workbook && (
                                <span className="text-slate-400">(exported before redownload was available)</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {p?.already_exported && (
                      <Badge className="bg-slate-700 ml-1" data-testid={`export-job-already-exported-badge-${j.job_id}`}>
                        <FileCheck2 className="h-3 w-3 mr-1" />Already exported before{p.already_exported_detail?.invoice_number != null ? ` (#${p.already_exported_detail.invoice_number})` : ''}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm text-slate-500">{p?.assigned_invoice_number ?? '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button onClick={runPreview} disabled={previewing || selectedIds.length === 0} data-testid="export-preview-btn">
          {previewing ? 'Previewing…' : `Preview export (${selectedIds.length} selected)`}
        </Button>
        {preview && (
          <Button onClick={runConfirm} disabled={downloading || !preview.can_confirm} data-testid="export-download-btn">
            <Download className="h-4 w-4 mr-2" />{downloading ? 'Preparing…' : 'Download staging file'}
          </Button>
        )}
      </div>

      {preview && (
        <div className="space-y-2" data-testid="export-preview-summary">
          {!preview.can_confirm && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2 flex items-start gap-2" data-testid="export-blocked-notice">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>Every selected job must be Ready before a file can be generated — deselect or resolve the blocked job(s) above and preview again.</span>
            </div>
          )}
          {preview.missing_ids?.length > 0 && (
            <div className="text-sm text-red-600" data-testid="export-missing-ids-notice">Not found: job id(s) {preview.missing_ids.join(', ')}.</div>
          )}
          {preview.repeat_export_job_ids?.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2" data-testid="export-repeat-warning">
              {preview.repeat_export_job_ids.length} selected job(s) were already exported before — blocked from a new staging export (see "Redownload original" next to each one above).
            </div>
          )}
          {preview.number_reuse_warning && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 text-orange-800 text-sm px-3 py-2" data-testid="export-number-reuse-warning">
              Starting number {preview.starting_invoice_number} is at or below a previously used number ({preview.highest_prior_invoice_number}) — warning only, not blocked.
            </div>
          )}
          {preview.can_confirm && (
            <div className="text-sm text-slate-600" data-testid="export-totals">
              {preview.rows_preview.length} job(s) ready · Net £{preview.net_total.toFixed(2)} · VAT £{preview.vat_total.toFixed(2)} · Total £{preview.total_inc_vat.toFixed(2)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
